import { useState, useEffect, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { fmt } from '@/lib/format';

type Almacen = { id: string; clave: string; nombre: string };
type Articulo = { id: string; clave: string; descripcion: string; unidad_clave?: string; partida_cog_id?: string; costo_promedio: number };
type Proveedor = { id: string; rfc: string; razon_social: string };
type PresupEg = { id: string; partida_codigo: string; partida_nombre: string; disponible: number };

type Linea = {
  articulo_id: string;
  articulo_label: string;
  cantidad: number;
  costo_unitario: number;
  partida_cog_id: string;
  lote: string;
};

export function NuevoMovimientoModal({
  open, tipo, onClose, onCreated,
}: {
  open: boolean;
  tipo: 'entrada' | 'salida';
  onClose: () => void;
  onCreated: () => void;
}) {
  const [almacenID, setAlmacenID] = useState('');
  const [tipoDetalle, setTipoDetalle] = useState(tipo === 'entrada' ? 'entrada_compra' : 'salida_consumo');
  const [proveedorID, setProveedorID] = useState('');
  const [concepto, setConcepto] = useState('');
  const [iva, setIva] = useState(0);
  const [presupuestoID, setPresupuestoID] = useState('');
  const [afectarPresup, setAfectarPresup] = useState(true);
  const [crearCxP, setCrearCxP] = useState(true);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [articuloPick, setArticuloPick] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  useEffect(() => {
    setTipoDetalle(tipo === 'entrada' ? 'entrada_compra' : 'salida_consumo');
    setLineas([]);
    setErr(null);
  }, [tipo, open]);

  const { data: almacenes } = useQuery({ queryKey: ['almacenes'], queryFn: () => api<Almacen[]>('/almacenes'), enabled: open });
  const { data: articulos } = useQuery({ queryKey: ['articulos-pick'], queryFn: () => api<{items:Articulo[]}>('/articulos?limit=500'), enabled: open });
  const { data: proveedores } = useQuery({ queryKey: ['proveedores-pick'], queryFn: () => api<{items:Proveedor[]}>('/proveedores?limit=500&solo_activos=true'), enabled: open && tipo === 'entrada' });
  const { data: presupuestos } = useQuery({ queryKey: ['presupuestos-egresos'], queryFn: () => api<PresupEg[]>('/presupuesto/egresos'), enabled: open });

  function agregarLinea() {
    if (!articuloPick) return;
    const art = articulos?.items.find(a => a.id === articuloPick);
    if (!art) return;
    setLineas([...lineas, {
      articulo_id: art.id,
      articulo_label: `${art.clave} — ${art.descripcion}`,
      cantidad: 1,
      costo_unitario: tipo === 'entrada' ? 0 : art.costo_promedio,
      partida_cog_id: art.partida_cog_id ?? '',
      lote: '',
    }]);
    setArticuloPick('');
  }

  function quitarLinea(i: number) {
    setLineas(lineas.filter((_, idx) => idx !== i));
  }

  function actualizarLinea(i: number, campo: keyof Linea, valor: any) {
    setLineas(lineas.map((l, idx) => idx === i ? { ...l, [campo]: valor } : l));
  }

  const subtotal = lineas.reduce((acc, l) => acc + l.cantidad * l.costo_unitario, 0);
  const total = subtotal + iva;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lineas.length === 0) { setErr('Agrega al menos una línea'); return; }
    if (!almacenID) { setErr('Selecciona almacén'); return; }
    setSubm(true); setErr(null);
    try {
      const body = {
        almacen_id: almacenID,
        tipo: tipoDetalle,
        proveedor_id: tipo === 'entrada' ? proveedorID : undefined,
        concepto,
        iva,
        afectar_presupuesto: afectarPresup,
        presupuesto_egreso_id: presupuestoID || undefined,
        crear_cxp: tipo === 'entrada' && crearCxP && !!proveedorID,
        lineas: lineas.map(l => ({
          articulo_id: l.articulo_id,
          cantidad: l.cantidad,
          costo_unitario: l.costo_unitario,
          partida_cog_id: l.partida_cog_id || undefined,
          lote: l.lote || undefined,
        })),
      };
      await api('/almacen/movimientos', { method: 'POST', body });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally {
      setSubm(false);
    }
  }

  const esEntrada = tipo === 'entrada';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={esEntrada ? 'Nueva entrada al almacén' : 'Nueva salida del almacén'}
      subtitle={esEntrada
        ? 'Genera póliza automática 1.1.5 Almacén D / 2.1.2 Proveedores H, crea CxP y afecta presupuesto devengado.'
        : 'Genera póliza automática 5.x Gasto D / 1.1.5 Almacén H y afecta presupuesto ejercido.'}
      size="xl"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="mov-form" loading={subm}>
            Registrar {esEntrada ? 'entrada' : 'salida'} · {fmt.money(total)}
          </Button>
        </>
      }
    >
      <form id="mov-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">{err}</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="text-[13px] font-medium text-ink">Almacén</label>
            <select value={almacenID} onChange={(e) => setAlmacenID(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(seleccione)</option>
              {(almacenes ?? []).map(a => <option key={a.id} value={a.id}>{a.clave} — {a.nombre}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[13px] font-medium text-ink">Tipo</label>
            <select value={tipoDetalle} onChange={(e) => setTipoDetalle(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              {esEntrada ? (
                <>
                  <option value="entrada_compra">Entrada por compra</option>
                  <option value="entrada_donacion">Entrada por donación</option>
                  <option value="entrada_devolucion">Entrada por devolución</option>
                  <option value="entrada_ajuste">Ajuste positivo (sobrante)</option>
                </>
              ) : (
                <>
                  <option value="salida_consumo">Salida por consumo (vale)</option>
                  <option value="salida_baja">Salida por baja</option>
                  <option value="salida_ajuste">Ajuste negativo (merma)</option>
                  <option value="salida_devolucion_proveedor">Devolución a proveedor</option>
                  <option value="salida_donacion">Donación</option>
                </>
              )}
            </select>
          </div>

          {esEntrada && (
            <div>
              <label className="text-[13px] font-medium text-ink">Proveedor</label>
              <select value={proveedorID} onChange={(e) => setProveedorID(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
                <option value="">(ninguno)</option>
                {(proveedores?.items ?? []).map(p => <option key={p.id} value={p.id}>{p.rfc} — {p.razon_social}</option>)}
              </select>
            </div>
          )}

          <div className="sm:col-span-3">
            <label className="text-[13px] font-medium text-ink">Presupuesto (partida COG a afectar)</label>
            <select value={presupuestoID} onChange={(e) => setPresupuestoID(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(sin afectar presupuesto)</option>
              {(presupuestos ?? []).map(p => (
                <option key={p.id} value={p.id}>
                  {p.partida_codigo} — {p.partida_nombre} · disponible {fmt.money(p.disponible)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Líneas</h4>
            <div className="flex items-center gap-2">
              <select value={articuloPick} onChange={(e) => setArticuloPick(e.target.value)}
                className="h-9 rounded-md border border-border bg-surface px-2 text-[13px] shadow-subtle">
                <option value="">(elegir artículo)</option>
                {(articulos?.items ?? []).map(a => (
                  <option key={a.id} value={a.id}>{a.clave} — {a.descripcion.slice(0, 50)}</option>
                ))}
              </select>
              <Button size="sm" variant="secondary" type="button" onClick={agregarLinea}>
                <Plus size={14} /> Añadir
              </Button>
            </div>
          </div>

          <div className="mt-2 overflow-hidden rounded-md border border-border">
            {lineas.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-ink-mute">Sin líneas. Añade artículos arriba.</div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
                  <tr>
                    <th className="px-3 py-2">Artículo</th>
                    <th className="px-3 py-2 text-right">Cantidad</th>
                    <th className="px-3 py-2 text-right">Costo</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2">Lote</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lineas.map((l, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 max-w-[260px] truncate">{l.articulo_label}</td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.001" value={l.cantidad}
                          onChange={(e) => actualizarLinea(i, 'cantidad', parseFloat(e.target.value) || 0)}
                          className="num h-8 w-24 rounded border border-border bg-surface px-2 text-right" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.0001" value={l.costo_unitario}
                          disabled={!esEntrada}
                          onChange={(e) => actualizarLinea(i, 'costo_unitario', parseFloat(e.target.value) || 0)}
                          className="num h-8 w-28 rounded border border-border bg-surface px-2 text-right disabled:opacity-60" />
                      </td>
                      <td className="num px-3 py-2 text-right font-semibold">
                        {fmt.money(l.cantidad * l.costo_unitario)}
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" value={l.lote} placeholder="opcional"
                          onChange={(e) => actualizarLinea(i, 'lote', e.target.value)}
                          className="h-8 w-28 rounded border border-border bg-surface px-2" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => quitarLinea(i)}
                          className="rounded p-1 text-ink-mute hover:bg-muted hover:text-danger">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/40">
                    <td colSpan={3} className="px-3 py-2 text-right text-[11px] font-semibold uppercase text-ink-mute">Subtotal</td>
                    <td className="num px-3 py-2 text-right font-semibold">{fmt.money(subtotal)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input name="concepto" label="Concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          {esEntrada && (
            <Input name="iva" label="IVA" type="number" step="0.01" value={iva}
                   onChange={(e) => setIva(parseFloat(e.target.value) || 0)} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-6 text-[13px]">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={afectarPresup}
                   onChange={(e) => setAfectarPresup(e.target.checked)}
                   className="h-3.5 w-3.5 accent-brand-500" />
            Afectar presupuesto ({esEntrada ? 'devengado' : 'ejercido'})
          </label>
          {esEntrada && (
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={crearCxP}
                     onChange={(e) => setCrearCxP(e.target.checked)}
                     className="h-3.5 w-3.5 accent-brand-500" />
              Crear cuenta por pagar
            </label>
          )}
        </div>
      </form>
    </Modal>
  );
}
